import { ComponentProps, ComponentType, FC } from "react";
import { GestureResponderEvent } from "react-native";
import { useSegments } from "expo-router";
import { type VariantProps } from "class-variance-authority";
// eslint-disable-next-line lucide-icon-suffix/enforce-icon-suffix
import type { LucideIcon, LucideProps } from "lucide-react-native";

import { TextClassContext } from "@/context/TextClassContext";
import { useAppTheme } from "@/hooks/useAppTheme";
import { ChildrenOrI18nType } from "@/i18n";
import { trackElementClicked } from "@/services/mixpanel";
import { cn } from "@/utils/tailwind";

import { VnIcon } from "../VnIcon";
import { VnLoadingSpinner } from "../VnLoadingSpinner";
import { TextVariantType } from "../VnText/variants";
import { VnText } from "../VnText/VnText";
import { VnTouchableOpacity } from "../VnTouchableOpacity";
import {
  buttonTextVariants,
  buttonVariants,
  ButtonVariantType,
  DISABLED_TEXT,
} from "./variants";

type VnButtonIconPropsType = {
  size?: number;
  variant: ButtonVariantType;
  isLoading: boolean;
  IconComponent?: LucideIcon | ComponentType<LucideProps>;
  iconProps?: Partial<ComponentProps<typeof VnIcon>>;
};

const VnButtonIcon: FC<VnButtonIconPropsType> = ({
  size = 14,
  isLoading,
  variant,
  IconComponent,
  iconProps = {},
}) => {
  const { resolvedColors } = useAppTheme();

  switch (true) {
    case isLoading:
      return (
        <VnLoadingSpinner
          {...iconProps}
          size={size + 2}
          fill={
            variant === "default"
              ? resolvedColors.primaryForeground
              : resolvedColors.foreground
          }
        />
      );

    case Boolean(IconComponent):
      return IconComponent ? (
        <VnIcon
          {...iconProps}
          as={IconComponent}
          size={size}
          className={cn(DISABLED_TEXT, iconProps?.className)}
        />
      ) : null;

    default:
      return null;
  }
};

export type VnButtonPropsType = ComponentProps<typeof VnTouchableOpacity> &
  ChildrenOrI18nType &
  VariantProps<typeof buttonVariants> & {
    textVariant?: TextVariantType;
    textClassName?: string;
    isLoading?: boolean;
    isDisabled?: boolean;
    IconComponent?: LucideIcon | ComponentType<LucideProps>;
    iconProps?: Partial<ComponentProps<typeof VnIcon>>;
    iconPosition?: "left" | "right";
  };

export const VnButton: FC<VnButtonPropsType> = ({
  className,
  variant = "default",
  size,
  IconComponent,
  iconPosition = "left",
  iconProps,
  textVariant = "button",
  textClassName,
  isLoading = false,
  isDisabled = false,
  children,
  i18nKey,
  i18nOptions,
  onPress,
  ...props
}) => {
  const segments = useSegments();

  const handlePress = (e: GestureResponderEvent) => {
    if (isDisabled || isLoading) {
      return;
    }
    trackElementClicked({
      route: `/${segments.join("/")}`,
      elementTextKey: i18nKey,
      elementText: typeof children === "string" ? children : undefined,
      elementType: "button",
    });
    onPress?.(e);
  };

  return (
    <TextClassContext.Provider value={buttonTextVariants({ variant, size })}>
      <VnTouchableOpacity
        className={cn(buttonVariants({ variant, size }), className, {
          "flex-row-reverse": iconPosition === "right" && !isLoading,
        })}
        role="button"
        disabled={isDisabled || isLoading}
        onPress={handlePress}
        {...props}>
        <VnButtonIcon
          variant={variant}
          size={size === "lg" ? 16 : 14}
          isLoading={isLoading}
          IconComponent={IconComponent}
          iconProps={iconProps}
        />
        {children ?? (
          <VnText
            variant={textVariant}
            i18nKey={i18nKey}
            i18nOptions={i18nOptions}
            className={textClassName}
          />
        )}
      </VnTouchableOpacity>
    </TextClassContext.Provider>
  );
};
